import { useState } from 'react';
import { Alert, Badge, Button, Card, FileInput, Group, Paper, Select, SimpleGrid, Stack, Table, Tabs, Text, TextInput } from '@mantine/core';
import { IconFileSpreadsheet, IconUpload } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ErrorApi } from '../api';
import { avisarError, avisarOk, Cabecera, EJERCICIO_ACTUAL, euros, fechaCorta, OPCIONES_EJERCICIO } from '../comun';

const COLOR_ESTADO = { ok: 'teal', aviso: 'yellow', omitida: 'gray', error: 'red' } as const;

interface FilaVista {
  hoja?: string;
  fila: number;
  estado: keyof typeof COLOR_ESTADO;
  mensajes: string[];
  fecha: string | null;
  descripcion: string | null;
  tipo?: string;
  total?: number;
  importe?: number;
  codigo?: string;
}

interface Respuesta {
  modo: 'previsualizacion' | 'confirmada';
  avisos: string[];
  resumen: Record<string, number | unknown>;
  filas: FilaVista[];
  mapeo?: Record<string, string | null>;
}

export function PaginaImportar() {
  return (
    <>
      <Cabecera titulo="Importar" descripcion="Carga tu libro Excel anterior o los cobros con tarjeta de SumUp. Primero se previsualiza; nada se guarda hasta que confirmes." />
      <Tabs defaultValue="excel">
        <Tabs.List mb="md">
          <Tabs.Tab value="excel" leftSection={<IconFileSpreadsheet size={16} />}>
            Libro Excel anterior
          </Tabs.Tab>
          <Tabs.Tab value="sumup">SumUp (CSV)</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="excel">
          <Importador
            ruta="/importacion/libro-excel"
            acepta=".xlsx"
            explicacion="Hojas «Ingresos» y «Gastos» del libro de ingresos y gastos. La hoja «Resumen» no se importa: la aplicación lo calcula. Las filas bajo «PENDIENTES» se marcan como gastos pendientes de justificar."
          />
        </Tabs.Panel>
        <Tabs.Panel value="sumup">
          <Importador
            ruta="/importacion/sumup-csv"
            acepta=".csv,.txt"
            explicacion="En el panel web de SumUp, exporta el historial de transacciones en CSV. Cada cobro se registra como venta con tarjeta (sin duplicar si repites la importación) y las comisiones de cada mes como gasto pendiente de justificar con la factura de SumUp."
          />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

function Importador({ ruta, acepta, explicacion }: { ruta: string; acepta: string; explicacion: string }) {
  const qc = useQueryClient();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [ejercicio, setEjercicio] = useState(String(EJERCICIO_ACTUAL));
  const [motivo, setMotivo] = useState('');
  const [vista, setVista] = useState<Respuesta | null>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async (modo: 'previsualizar' | 'confirmar') => {
    if (!archivo) return;
    setEnviando(true);
    try {
      const f = new FormData();
      f.append('archivo', archivo, archivo.name);
      const q = new URLSearchParams({ ejercicio, modo, ...(motivo.trim().length >= 5 ? { motivoRectificacion: motivo.trim() } : {}) });
      const r = await api.subir<Respuesta>(`${ruta}?${q}`, f);
      setVista(r);
      if (modo === 'confirmar') {
        avisarOk(`${String(r.resumen.creados ?? 0)} movimientos importados`, 'Importación completada');
        await qc.invalidateQueries();
      }
    } catch (e) {
      avisarError(e instanceof ErrorApi ? e : new Error(String(e)));
    } finally {
      setEnviando(false);
    }
  };

  const bloqueadas = vista?.filas.some((f) => f.mensajes.some((m) => m.includes('ya declarado'))) ?? false;
  const validas = vista?.filas.filter((f) => f.estado === 'ok' || f.estado === 'aviso').length ?? 0;

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        {explicacion}
      </Text>
      <Paper withBorder p="sm">
        <Group align="flex-end" wrap="wrap">
          <FileInput
            label="Archivo"
            accept={acepta}
            value={archivo}
            onChange={(f) => {
              setArchivo(f);
              setVista(null);
            }}
            placeholder="Elegir archivo"
            style={{ flex: 1, minWidth: 220 }}
            clearable
          />
          <Select label="Ejercicio" w={110} data={OPCIONES_EJERCICIO} value={ejercicio} onChange={(v) => setEjercicio(v ?? String(EJERCICIO_ACTUAL))} allowDeselect={false} />
          <Button leftSection={<IconUpload size={16} />} disabled={!archivo} loading={enviando} onClick={() => void enviar('previsualizar')}>
            Previsualizar
          </Button>
        </Group>
      </Paper>

      {vista && (
        <>
          {vista.avisos.map((a) => (
            <Alert key={a} color="yellow">
              {a}
            </Alert>
          ))}
          {vista.mapeo && (
            <Text size="xs" c="dimmed">
              Columnas detectadas: {Object.entries(vista.mapeo).map(([k, v]) => `${k} → ${v ?? '(no encontrada)'}`).join(' · ')}
            </Text>
          )}
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            {(['ok', 'avisos', 'omitidas', 'errores'] as const).map((k) => (
              <Card key={k} withBorder padding="xs">
                <Text size="xs" c="dimmed">
                  {{ ok: 'Correctas', avisos: 'Con avisos', omitidas: 'Omitidas', errores: 'Con errores' }[k]}
                </Text>
                <Text fw={700} fz="lg">
                  {String(vista.resumen[k] ?? 0)}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
          {bloqueadas && (
            <Alert color="orange">
              Algunas filas son de periodos ya declarados. Si es la carga inicial de datos ya presentados, indica un motivo (por ejemplo «Carga inicial del libro anterior») y vuelve a previsualizar.
              <TextInput mt="xs" placeholder="Motivo de la rectificación" value={motivo} onChange={(e) => setMotivo(e.currentTarget.value)} />
            </Alert>
          )}
          <Paper withBorder>
            <Table.ScrollContainer minWidth={700}>
              <Table verticalSpacing={4}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Fila</Table.Th>
                    <Table.Th>Estado</Table.Th>
                    <Table.Th>Fecha</Table.Th>
                    <Table.Th>Descripción</Table.Th>
                    <Table.Th ta="right">Importe</Table.Th>
                    <Table.Th>Mensajes</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {vista.filas.map((f) => (
                    <Table.Tr key={`${f.hoja ?? ''}${f.fila}`}>
                      <Table.Td>
                        <Text size="xs">
                          {f.hoja ? `${f.hoja} ` : ''}
                          {f.fila}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={COLOR_ESTADO[f.estado]} size="sm" variant="light">
                          {f.estado}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{fechaCorta(f.fecha)}</Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>
                          {f.descripcion ?? f.codigo ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">{euros(f.total ?? f.importe)}</Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {f.mensajes.join(' · ')}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
          {vista.modo === 'previsualizacion' && (
            <Group justify="flex-end">
              <Button disabled={validas === 0} loading={enviando} onClick={() => void enviar('confirmar')}>
                Importar {validas} fila(s)
              </Button>
            </Group>
          )}
        </>
      )}
    </Stack>
  );
}
